import React, { useCallback, useEffect, useState } from "react";
import { Camera, Loader2, RefreshCw, Upload } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";

const when = (iso) => {
  if (!iso) return "never captured";
  const days = Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
  if (days <= 0) return "captured today";
  if (days === 1) return "captured yesterday";
  return `captured ${days} days ago`;
};

export const GuideScreenshots = () => {
  const [shots, setShots] = useState([]);
  const [busy, setBusy] = useState("");
  const [stamp, setStamp] = useState(Date.now());

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/help-screenshots");
      setShots(data.screenshots);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const recapture = async (keys) => {
    setBusy(keys || "all");
    try {
      const { data } = await api.post("/admin/help-screenshots/refresh",
        null, { params: keys ? { keys } : {}, timeout: 240000 });
      const failures = Object.entries(data.captured).filter(([, v]) => v !== "ok");
      const ok = Object.values(data.captured).filter((v) => v === "ok").length;
      if (ok) toast.success(`Re-captured ${ok} screenshot${ok === 1 ? "" : "s"}`);
      failures.forEach(([key, reason]) => toast.error(`${key}: ${reason}`));
      setStamp(Date.now());
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "The capture run failed.");
    } finally {
      setBusy("");
    }
  };

  const upload = async (key, event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    setBusy(key);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/admin/help-screenshots/${key}`, fd,
        { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${key} image replaced`);
      setStamp(Date.now());
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy("");
    }
  };

  return (
    <div className="border p-6" style={{ borderColor: "var(--border)" }} data-testid="guide-screenshots">
      <div className="mb-4 flex items-start justify-between gap-4">
        <div>
          <div className="eyebrow">Guide images</div>
          <p className="mt-1 text-xs" style={{ color: "var(--muted)" }}>
            Screenshots embedded in the PDF guides. Re-capture them after a UI change.
          </p>
        </div>
        <button
          onClick={() => recapture("")}
          disabled={Boolean(busy)}
          className="btn-primary flex shrink-0 items-center gap-2"
          data-testid="recapture-all"
        >
          {busy === "all" ? <Loader2 className="animate-spin" size={14}/> : <RefreshCw size={14}/>}
          {busy === "all" ? "Capturing…" : "Re-capture all"}
        </button>
      </div>

      <div className="divide-y border" style={{ borderColor: "var(--border)" }}>
        {shots.map((shot) => (
          <div key={shot.key} className="flex items-center gap-3 p-3" data-testid={`guide-shot-${shot.key}`}>
            <img
              src={shot.captured_at
                ? `${process.env.REACT_APP_BACKEND_URL}/api/admin/help-screenshots/${shot.key}/image?v=${stamp}`
                : ""}
              alt=""
              className="h-10 w-16 shrink-0 border object-cover"
              style={{ borderColor: "var(--border)", background: "var(--surface)" }}
            />
            <div className="min-w-0 flex-1">
              <div className="truncate text-sm font-medium" style={{ color: "var(--ink)" }}>{shot.key}</div>
              <div className="text-xs" style={{ color: shot.captured_at ? "var(--muted)" : "#B42318" }}>
                {shot.route} · {when(shot.captured_at)}
              </div>
            </div>
            <button
              onClick={() => recapture(shot.key)}
              disabled={Boolean(busy)}
              className="btn-ghost !p-2"
              title="Re-capture this screen"
              data-testid={`recapture-${shot.key}`}
            >
              {busy === shot.key ? <Loader2 className="animate-spin" size={13}/> : <Camera size={13}/>}
            </button>
            <label className="btn-ghost !p-2 cursor-pointer" title="Upload a replacement PNG">
              <Upload size={13}/>
              <input type="file" accept="image/png" className="hidden"
                     onChange={(e) => upload(shot.key, e)} data-testid={`upload-${shot.key}`}/>
            </label>
          </div>
        ))}
      </div>
      <p className="mt-3 text-xs" style={{ color: "var(--muted)" }}>
        Capture signs in with a short-lived session that is revoked straight afterwards. Client
        screens are captured from the demo client account.
      </p>
    </div>
  );
};

export default GuideScreenshots;
